/**
 * Pinia store for the contextual NOTAMs feature (#8), rewritten under #54
 * and simplified under PR #55 R4.
 *
 * Simplification rationale (PR #55 R4 review): three earlier design
 * choices — D5 (preserve currentHits across stop), D10 (hitsAreCurrentForPosition
 * gate), and effectiveLateralNm — encoded safety-motivated smart behavior
 * but each created a distinct class of edge case that recursive review
 * kept surfacing. This revision drops all three and treats every /tracking
 * mount as a fresh session.
 *
 * Trade-offs (accepted):
 *   - Pulse continuity across nav is lost. Pilots rarely leave /tracking
 *     mid-flight; when they do, "new NOTAM" pulses restart from scratch.
 *   - Bubble ring can visually disagree with dots for up to forcedRefreshMs
 *     while a settings change waits for the next successful call. During
 *     an API outage the mismatch persists until backoff clears + next call.
 *   - Stale-session state (banners, backoff schedule) is not preserved
 *     across nav — a brief accidental route hop resets the API-error
 *     banner, but the next failing call will re-arm it within
 *     forcedRefreshMs.
 *
 * Two independent axes stay:
 *   gpsState:  'unknown' | 'transient' | 'persistent' | 'ok'
 *   apiState:  'ok' | 'error'   (backoff derived from nextAllowedAt)
 *   isStale = gpsState !== 'ok' || (nowMs - lastFixAt) > staleAgeMs
 *
 * Setters remain axis-specific — that IS the R2/R3-blocking safety
 * invariant and stays.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ContextualNotamHit } from '@/types/notam';
import { config } from '@/config/config';

export type GpsState = 'unknown' | 'transient' | 'persistent' | 'ok';
export type ApiState = 'ok' | 'error';

export interface PositionSnapshot {
  lat: number;
  lon: number;
  altFt: number | null;
  accuracy?: number;
  headingDeg: number | null;
  speedKt: number | null;
}

export const useContextualStore = defineStore('contextual', () => {
  // --- State axes -------------------------------------------------------
  const gpsState = ref<GpsState>('unknown');
  const apiState = ref<ApiState>('ok');
  const gpsError = ref<string | null>(null);
  const apiError = ref<string | null>(null);

  const lastFixAt = ref<number | null>(null);
  // #527 — copied from the active position source (PositionProvider.fixesAge);
  // reset() restores the real receiver's value, as every stop reverts to it.
  const fixesAge = ref(true);
  // #527 — set when a position was CARRIED across a source swap (approximate
  // → real) rather than delivered by the current source. Cleared by the
  // first delivered fix. While set, the view keeps the approximate banner
  // and the GPS-stale banner stays off: nothing has gone stale, nothing has
  // arrived yet.
  const carriedFrom = ref<'approximate' | null>(null);
  const lastPosition = ref<PositionSnapshot | null>(null);
  const lastQueriedAt = ref<string | null>(null);
  const nextAllowedAt = ref(0);

  const currentHits = ref<ContextualNotamHit[]>([]);
  const prevIds = ref<Set<string>>(new Set());

  // --- Single 1Hz wall-clock tick ---------------------------------------
  // The ONLY reactive wall-clock in the app. TrackingView's banner
  // countdowns and this store's isStale computed both read from `nowMs`.
  // Fixes R4 findings #14 and #15 (multiple tickers).
  const _nowMs = ref(Date.now());
  const _tickHandle: ReturnType<typeof setInterval> = setInterval(
    () => { _nowMs.value = Date.now(); },
    1000
  );
  const nowMs = computed(() => _nowMs.value);
  /** Test-only: stop the ticker so orphan intervals don't accumulate in vitest. */
  function stopStoreTicker(): void { clearInterval(_tickHandle); }

  // --- Derived state ----------------------------------------------------
  const isStale = computed(() => {
    // A carried position is waiting for the first real fix: 'unknown' is
    // not a fault yet, but a receiver that then FAILS still is.
    if (carriedFrom.value !== null) return gpsState.value === 'transient' || gpsState.value === 'persistent';
    if (gpsState.value !== 'ok') return true;
    if (lastFixAt.value === null) return false;
    if (!fixesAge.value) return false; // an approximate fix is old by design, not lost
    return (_nowMs.value - lastFixAt.value) > config.geolocation.staleAgeMs;
  });

  const newEntryIds = computed<Set<string>>(() => {
    const news = new Set<string>();
    for (const hit of currentHits.value) {
      if (!prevIds.value.has(hit.id)) news.add(hit.id);
    }
    return news;
  });

  // --- Setters ----------------------------------------------------------
  function setGpsState(next: GpsState, message: string | null = null): void {
    gpsState.value = next;
    if (message !== null) gpsError.value = message;
    if (next === 'ok') gpsError.value = null;
  }

  function setApiState(next: ApiState, message: string | null = null): void {
    apiState.value = next;
    if (message !== null) apiError.value = message;
    if (next === 'ok') apiError.value = null;
  }

  /**
   * Set a fresh snapshot. Never touches gpsState / apiState — the axis
   * separation invariant.
   */
  function setHits(hits: ContextualNotamHit[], queriedAt: string): void {
    const promoted = new Set<string>();
    for (const h of currentHits.value) promoted.add(h.id);
    prevIds.value = promoted;
    currentHits.value = hits;
    lastQueriedAt.value = queriedAt;
  }

  function setFixesAge(v: boolean): void {
    fixesAge.value = v;
  }

  function setPosition(pos: PositionSnapshot, fixTimestamp?: number): void {
    lastPosition.value = pos;
    lastFixAt.value = fixTimestamp ?? Date.now();
    carriedFrom.value = null; // a delivered fix supersedes anything carried
  }

  /** #527 — keep a position across a source swap, labelled with where it came from. */
  function carryPosition(pos: PositionSnapshot, from: 'approximate'): void {
    lastPosition.value = pos;
    lastFixAt.value = null; // not a fix of the current source: no age to report
    carriedFrom.value = from;
  }

  function setNextAllowedAt(v: number): void {
    nextAllowedAt.value = v;
  }

  /**
   * Full reset — called on every stop(). No preservation.
   *
   * PR #55 R4 simplification: dropping D5 (preserve currentHits + prevIds)
   * eliminates a whole class of "stale session state contradicts fresh
   * state on re-entry" bugs (R4 findings #2, #3, #4, #5, #11). The cost is
   * that pulse continuity across nav is lost. Accepted.
   */
  function reset(): void {
    gpsState.value = 'unknown';
    apiState.value = 'ok';
    gpsError.value = null;
    apiError.value = null;
    lastPosition.value = null;
    lastFixAt.value = null;
    carriedFrom.value = null;
    fixesAge.value = true;
    nextAllowedAt.value = 0;
    currentHits.value = [];
    prevIds.value = new Set();
    lastQueriedAt.value = null;
  }

  return {
    // state
    gpsState,
    apiState,
    gpsError,
    apiError,
    lastQueriedAt,
    lastPosition,
    lastFixAt,
    nextAllowedAt,
    currentHits,
    prevIds,
    // computed
    isStale,
    newEntryIds,
    nowMs,
    // actions
    setGpsState,
    setFixesAge,
    fixesAge,
    carryPosition,
    carriedFrom,
    setApiState,
    setHits,
    setPosition,
    setNextAllowedAt,
    reset,
    stopStoreTicker
  };
});
